import React, { createContext, useContext, useState, useMemo, useCallback } from 'react'
import { useDashboardData } from './use-dashboard-data'
import type { DashboardAnalytics } from '#app/lib/data'
import type { DashboardUser, DashboardTeam, DashboardWorkflow } from '../data/types'
import {
  DEFAULT_PRESET,
  resolveDatePreset,
  type DatePresetCustom,
  type DatePresetKey,
} from '#app/lib/utils/date-presets'

interface FilterState {
  presetKey: DatePresetKey
  customRange: DatePresetCustom | null
  startDate: string
  endDate: string
  selectedTeams: string[]
  selectedReps: string[]
  setPreset: (key: DatePresetKey, custom?: DatePresetCustom) => void
  setCustomRange: (range: DatePresetCustom | null) => void
  setSelectedTeams: (v: string[]) => void
  setSelectedReps: (v: string[]) => void
  analytics: DashboardAnalytics | null
  members: DashboardUser[]
  teams: DashboardTeam[]
  workflows: DashboardWorkflow[]
  teamNames: string[]
  loading: boolean
  initialLoading: boolean
  refetch: () => void
  getMemberName: (userId: string) => string
  getMemberTeam: (userId: string) => string
}

const FilterContext = createContext<FilterState | null>(null)

export function DashboardFilterProvider({ children }: { children: React.ReactNode }) {
  const [presetKey, setPresetKey] = useState<DatePresetKey>(DEFAULT_PRESET)
  const [customRange, setCustomRange] = useState<DatePresetCustom | null>(null)
  const [selectedTeams, setSelectedTeams] = useState<string[]>([])
  const [selectedReps, setSelectedReps] = useState<string[]>([])

  const { startDate, endDate } = useMemo(
    () => resolveDatePreset(presetKey, customRange ?? undefined),
    [presetKey, customRange],
  )

  const setPreset = useCallback(
    (key: DatePresetKey, custom?: DatePresetCustom) => {
      setPresetKey(key)
      if (custom) setCustomRange(custom)
      else if (key !== 'custom') setCustomRange(null)
    },
    [],
  )

  // Convert selected team names → teamIds for the backend query
  const [teamIdCache, setTeamIdCache] = useState<string[]>([])

  const {
    analytics,
    members,
    teams,
    workflows,
    loading,
    initialLoading,
    refetch,
  } = useDashboardData({
    startDate,
    endDate,
    teamIds: teamIdCache,
    memberIds: selectedReps,
  })

  // Keep teamIdCache in sync with selectedTeams + teams data
  const resolvedTeamIds = useMemo(() => {
    if (selectedTeams.length === 0) return []
    const ids: string[] = []
    for (const teamName of selectedTeams) {
      const team = teams.find(t => t.teamName === teamName)
      if (team) ids.push(team.teamId)
    }
    return ids
  }, [selectedTeams, teams])

  const resolvedKey = JSON.stringify(resolvedTeamIds)
  const cachedKey = JSON.stringify(teamIdCache)
  if (resolvedKey !== cachedKey) {
    setTeamIdCache(resolvedTeamIds)
  }

  const teamNames = useMemo(() => teams.map(t => t.teamName), [teams])

  const membersByUserId = useMemo(() => {
    const map = new Map<string, DashboardUser>()
    for (const m of members) map.set(m.userId, m)
    return map
  }, [members])

  const getMemberName = useCallback((userId: string) =>
    membersByUserId.get(userId)?.name ?? 'Unknown', [membersByUserId])

  const getMemberTeam = useCallback((userId: string) =>
    membersByUserId.get(userId)?.teams?.[0]?.teamName ?? 'No Team', [membersByUserId])

  const value = useMemo<FilterState>(() => ({
    presetKey,
    customRange,
    startDate,
    endDate,
    selectedTeams,
    selectedReps,
    setPreset,
    setCustomRange,
    setSelectedTeams,
    setSelectedReps,
    analytics,
    members,
    teams,
    workflows,
    teamNames,
    loading,
    initialLoading,
    refetch,
    getMemberName,
    getMemberTeam,
  }), [
    presetKey, customRange, startDate, endDate,
    selectedTeams, selectedReps, setPreset,
    analytics, members, teams, workflows, teamNames,
    loading, initialLoading, refetch, getMemberName, getMemberTeam,
  ])

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  )
}

export function useDashboardFilters() {
  const ctx = useContext(FilterContext)
  if (!ctx) throw new Error('useDashboardFilters must be used within DashboardFilterProvider')
  return ctx
}
